var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { BigNumber } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAssets, isSolanaVault, VaultList, vaultOptionToName, } from "shared/lib/constants/constants";
import { usePendingTransactions } from "shared/lib/hooks/pendingTransactionsContext";
import { getAssetDecimals, getAssetDisplay, getChainByVaultOption, } from "shared/lib/utils/asset";
import { formatBigNumber, isPracticallyZero } from "shared/lib/utils/math";
import { capitalize } from "shared/lib/utils/text";
import Toast from "shared/lib/components/Common/BaseToast";
import PendingToast from "./PendingToast";
import { getVaultColor } from "shared/lib/utils/vault";
import { useV2VaultsData } from "shared/lib/hooks/web3DataContext";
import { useVaultsPriceHistory } from "shared/lib/hooks/useVaultPerformanceUpdate";
import { parseUnits } from "ethers/lib/utils";
import { useChain } from "shared/lib/hooks/chainContext";
import { useHistory } from "react-router-dom";
export var TxStatusToast = function () {
    var _a = usePendingTransactions(), pendingTransactions = _a.pendingTransactions, transactionsCounter = _a.transactionsCounter;
    var t = useTranslation().t;
    var _b = useState(transactionsCounter), showedPendingTxCounter = _b[0], setShowPendingTxCounter = _b[1];
    var _c = useMemo(function () {
        var tailTx = pendingTransactions[pendingTransactions.length - 1];
        if (!tailTx) {
            return [undefined, undefined];
        }
        /**
         * We keep track of migrate type for processing statuses
         */
        if (tailTx.type === "migrate") {
            return [(tailTx.status || "processing"), tailTx];
        }
        return [tailTx.status, tailTx];
    }, [pendingTransactions]), status = _c[0], currentTx = _c[1];
    var getAmountFormatted = useCallback(function (_currentTx) {
        switch (_currentTx.type) {
            case "deposit":
            case "withdraw":
            case "withdrawInitiation":
            case "migrate":
                return formatBigNumber(BigNumber.from(_currentTx.amount), getAssetDecimals(getAssets(_currentTx.vault)));
            case "transfer":
                return formatBigNumber(BigNumber.from(_currentTx.amount), getAssetDecimals(getAssets(_currentTx.transferVault)));
            case "userCheckpoint":
                return undefined;
            case "protocolPenaltyClaim":
            case "protocolRevenueClaim":
                return undefined;
            default:
                return _currentTx.amount;
        }
    }, []);
    var getActionTitle = useCallback(function (_currentTx) {
        switch (_currentTx.type) {
            case "rewardClaim":
                return "Reward Claim";
            case "withdrawInitiation":
                return "WITHDRAWAL INITIATED";
            case "userCheckpoint":
                return "Apply Boost";
            default:
                return "" + capitalize(_currentTx.type);
        }
    }, []);
    var getSubtitle = useCallback(function (_currentTx) {
        var amountFormatted = getAmountFormatted(_currentTx);
        switch (_currentTx.type) {
            case "approval":
                return "Your " + getAssetDisplay(_currentTx.asset) + " is ready to deposit";
            case "stakingApproval":
                return "Your " + _currentTx.stakeAsset + " is ready to stake";
            case "withdrawInitiation":
                if (isSolanaVault(_currentTx.vault)) {
                    return "Initiated " + amountFormatted + " " + getAssetDisplay(getAssets(_currentTx.vault)) + " withdrawal from " + t("shared:ProductCopies:" + _currentTx.vault + ":title") + ". Your funds are automatically credited after 12pm UTC on Friday.";
                }
                return "Initiated " + amountFormatted + " " + getAssetDisplay(getAssets(_currentTx.vault)) + " withdrawal from " + t("shared:ProductCopies:" + _currentTx.vault + ":title") + ". You can complete your withdrawal any time after 12pm UTC on Friday.";
            case "withdraw":
                return amountFormatted + " " + getAssetDisplay(getAssets(_currentTx.vault)) + " withdrawn from " + t("shared:ProductCopies:" + _currentTx.vault + ":title");
            case "deposit":
                return amountFormatted + " " + getAssetDisplay(_currentTx.asset) + " deposited into " + t("shared:ProductCopies:" + _currentTx.vault + ":title");
            case "claim":
            case "rewardClaim":
                return amountFormatted + " $RBN claimed";
            case "stake":
                return amountFormatted + " " + _currentTx.stakeAsset + " staked";
            case "unstake":
                return amountFormatted + " " + _currentTx.stakeAsset + " unstaked";
            case "transfer":
                var receiveVault = _currentTx.receiveVault;
                return amountFormatted + " " + getAssetDisplay(getAssets(receiveVault)) + " transferred to " + t("shared:ProductCopies:" + receiveVault + ":title");
            case "migrate":
                return amountFormatted + " migrated to " + t("shared:ProductCopies:" + _currentTx.vault + ":title") + " V2";
            case "userCheckpoint":
                return "veRBN boost applied to " + _currentTx.vault;
            case "pause":
                return "Your position has been paused";
            case "resume":
                return "Your position has been resumed";
            default:
                return "";
        }
    }, [getAmountFormatted, t]);
    if (!status || !currentTx) {
        return null;
    }
    switch (status) {
        case "error":
            return (_jsx(Toast, { show: showedPendingTxCounter !== transactionsCounter, onClose: function () { return setShowPendingTxCounter(transactionsCounter); }, type: "error", title: getActionTitle(currentTx) + " failed", subtitle: (function () {
                    switch (currentTx.type) {
                        case "approval":
                            return "Please try approving " + getAssetDisplay(currentTx.asset) + " again";
                        case "stakingApproval":
                            return "Please try approving " + currentTx.stakeAsset + " again";
                        default:
                            return "Please resubmit transaction";
                    }
                })() }, void 0));
        case "processing":
            return (_jsx(PendingToast, { title: "MIGRATING FUNDS", color: currentTx.type === "migrate" ? getVaultColor(currentTx.vault) : "" }, void 0));
        case "success":
            return (_jsx(Toast, { show: showedPendingTxCounter !== transactionsCounter, onClose: function () { return setShowPendingTxCounter(transactionsCounter); }, type: currentTx.type === "claim" || currentTx.type === "rewardClaim"
                    ? "claim"
                    : "success", title: (function () {
                    switch (currentTx.type) {
                        case "pause":
                            return "position paused";
                        case "resume":
                            return "position resumed";
                        default:
                            return getActionTitle(currentTx) + " success";
                    }
                })(), 
                // title={currentTx.type === "pause"
                // ? "position paused"
                // : getActionTitle(currentTx) + " success"}
                subtitle: getSubtitle(currentTx) }, void 0));
    }
};
export var WithdrawReminderToast = function () {
    var data = useV2VaultsData().data;
    var chain = useChain()[0];
    var t = useTranslation().t;
    var history = useHistory();
    var _a = useState([]), reminders = _a[0], setReminders = _a[1];
    var _b = useState(0), showReminderIndex = _b[0], setShowReminderIndex = _b[1];
    var priceHistories = useVaultsPriceHistory().data;
    /**
     * Find vault that is ready to withdraw
     */
    useEffect(function () {
        VaultList.forEach(function (vault) {
            // Ignore if the chain is not the current chain
            if (getChainByVaultOption(vault) !== chain) {
                return;
            }
            var vaultData = data[vault];
            var asset = getAssets(vault);
            var decimals = getAssetDecimals(asset);
            var priceHistory = priceHistories.v2[vault].find(function (history) { return history.round === vaultData.withdrawals.round; });
            if (!isPracticallyZero(vaultData.withdrawals.shares, decimals) &&
                vaultData.withdrawals.round !== vaultData.round &&
                priceHistory) {
                /**
                 * Check if it had already indexed inside reminders
                 */
                if (reminders.find(function (reminder) {
                    return reminder.vault.option === vault && reminder.vault.version === "v2";
                })) {
                    return;
                }
                setReminders(function (curr) {
                    return curr.concat({
                        vault: { option: vault, version: "v2" },
                        amount: vaultData.withdrawals.shares
                            .mul(priceHistory.pricePerShare)
                            .div(parseUnits("1", getAssetDecimals(getAssets(vault)))),
                    });
                });
            }
        }, []);
    }, [data, priceHistories.v2, reminders, chain]);
    /**
     * Make sure index does not goes out of bound
     */
    useEffect(function () {
        var remindersNotShown = reminders.filter(function (reminder) { return !reminder.shown; }).length;
        if (showReminderIndex >= remindersNotShown) {
            setShowReminderIndex(Math.max(remindersNotShown - 1, 0));
        }
    }, [reminders, showReminderIndex]);
    var onClose = useCallback(function () {
        setReminders(function (curr) {
            return curr.map(function (reminder, index) {
                return index === showReminderIndex ? __assign(__assign({}, reminder), { shown: true }) : reminder;
            });
        });
    }, [showReminderIndex]);
    var navigateToVaultPage = useCallback(function (option, version) {
        onClose();
        if (version === "earn") {
            var vaultName = vaultOptionToName(option);
            var route = "/" + vaultName;
            history.push(route);
        }
        else {
            var versionRoute = version === "v1" ? "" : version + "/";
            // Eg. If version is v1, then "/theta-vault/T-ETH-C"
            // If v2, then "/v2/theta-vault/T-ETH-C"
            var vaultName = vaultOptionToName(option);
            var fullRoute = "/" + versionRoute + "theta-vault/" + vaultName;
            history.push(fullRoute);
        }
    }, [onClose, history]);
    if (reminders.filter(function (reminder) { return !reminder.shown; }).length > 0) {
        var currentReminder = reminders[showReminderIndex];
        var amount = currentReminder.amount, vault = currentReminder.vault;
        var option_1 = vault.option, version_1 = vault.version;
        return (_jsx(Toast, { show: true, onClose: onClose, type: "reminder", title: "COMPLETE YOUR WITHDRAWALS", subtitle: "You can now complete your withdrawal of " + formatBigNumber(amount, getAssetDecimals(getAssets(option_1))) + " " + getAssetDisplay(getAssets(option_1)) + " from the " + t("shared:ProductCopies:" + option_1 + ":title") + " vault", extra: { vaultOption: option_1 }, onClick: function () {
                navigateToVaultPage(option_1, version_1);
            } }, void 0));
    }
    return _jsx(_Fragment, {}, void 0);
};
//# sourceMappingURL=toasts.js.map